import React, { useState, useEffect } from 'react';
import { 
  UserCog, Plus, Edit2, Trash2, Search, X, Key, Copy, Check, 
  BookOpen, Users, Loader2, Download
} from 'lucide-react';
import { teachersAPI, groupsAPI, subjectsAPI } from '../services/api';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';

const TeachersPage = () => {
  const [teachers, setTeachers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', middle_name: '', 
    phone: '', experience_years: 0, subject_ids: [], group_ids: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchTeachers();
    fetchGroups();
    fetchSubjects();
  }, [currentPage]);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage };
      if (searchTerm) params.search = searchTerm;
      const response = await teachersAPI.getAll(params);
      const data = response.data;
      if (data.results) {
        setTeachers(data.results);
        setTotalCount(data.count || 0);
      } else {
        setTeachers(Array.isArray(data) ? data : []);
        setTotalCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error("O'qituvchilarni yuklashda xato:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupsAPI.getSimple();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Guruhlarni yuklashda xato:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await subjectsAPI.getSimple();
      setSubjects(response.data.subjects || []);
    } catch (error) {
      console.error('Fanlarni yuklashda xato:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeacher) {
        await teachersAPI.update(editingTeacher.id, formData);
      } else {
        await teachersAPI.create(formData);
      }
      fetchTeachers();
      closeModal();
    } catch (error) {
      console.error('Saqlashda xato:', error);
      alert(error.response?.data?.detail || 'Xatolik yuz berdi');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("O'qituvchini o'chirishni tasdiqlaysizmi?")) {
      try {
        await teachersAPI.delete(id);
        fetchTeachers();
      } catch (error) {
        console.error("O'chirishda xato:", error);
      }
    }
  };

  const handleResetPassword = async (id) => {
    if (window.confirm("Parolni yangilashni tasdiqlaysizmi?")) {
      try {
        await teachersAPI.resetPassword(id);
        fetchTeachers();
      } catch (error) {
        console.error("Parolni yangilashda xato:", error);
      }
    }
  };

  const handleAssignSubjects = async (subjectIds) => {
    try {
      await teachersAPI.assignSubjects(selectedTeacher.id, subjectIds);
      fetchTeachers();
      setShowAssignModal(null);
    } catch (error) {
      console.error("Fanlarni biriktirishda xato:", error);
    }
  };

  const handleAssignGroups = async (groupIds) => {
    try {
      await teachersAPI.assignGroups(selectedTeacher.id, groupIds);
      fetchTeachers();
      setShowAssignModal(null);
    } catch (error) {
      console.error("Guruhlarni biriktirishda xato:", error);
    }
  };

  const copyCredentials = (teacher) => {
    const text = `Login: ${teacher.user.username}\nParol: ${teacher.user.plain_password || '***'}`;
    navigator.clipboard.writeText(text);
    setCopiedId(teacher.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const exportToExcel = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const data = teachers.map((teacher, index) => ({
      '№': startIndex + index + 1,
      'Familiya': teacher.user.last_name || '',
      'Ism': teacher.user.first_name || '',
      'Sharifi': teacher.user.middle_name || '',
      'Tajriba (yil)': teacher.experience_years || 0,
      'Fanlar': teacher.subjects?.map(s => s.name).join(', ') || '',
      'Guruhlar': teacher.groups?.map(g => g.display_name).join(', ') || '',
      'Telefon': teacher.user.phone || '',
      'Login': teacher.user.username || '',
      'Parol': teacher.user.plain_password || ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "O'qituvchilar");
    
    // Ustun kengliklarini sozlash
    ws['!cols'] = [
      { wch: 5 },  // №
      { wch: 15 }, // Familiya
      { wch: 15 }, // Ism
      { wch: 15 }, // Sharifi
      { wch: 12 }, // Tajriba
      { wch: 25 }, // Fanlar
      { wch: 25 }, // Guruhlar
      { wch: 15 }, // Telefon
      { wch: 20 }, // Login
      { wch: 12 }  // Parol
    ];
    
    XLSX.writeFile(wb, `oqituvchilar_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const openModal = (teacher = null) => {
    if (teacher) {
      setEditingTeacher(teacher);
      setFormData({
        first_name: teacher.user.first_name,
        last_name: teacher.user.last_name,
        middle_name: teacher.user.middle_name || '',
        phone: teacher.user.phone || '',
        experience_years: teacher.experience_years || 0,
        subject_ids: teacher.subjects?.map(s => s.id) || [],
        group_ids: teacher.groups?.map(g => g.id) || []
      });
    } else {
      setEditingTeacher(null);
      setFormData({
        first_name: '', last_name: '', middle_name: '',
        phone: '', experience_years: 0, subject_ids: [], group_ids: []
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTeacher(null);
  };

  const openAssignModal = (teacher, type) => {
    setSelectedTeacher(teacher);
    setShowAssignModal(type);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchTeachers();
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><UserCog size={28} /> O'qituvchilar</h1>
          <p>Barcha o'qituvchilar ro'yxati</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={exportToExcel}>
            <Download size={20} /> Excel
          </button>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <Plus size={20} /> Yangi o'qituvchi
          </button>
        </div>
      </header>

      {/* Search */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="O'qituvchini qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="btn btn-sm btn-primary" onClick={handleSearch}>
            Qidirish
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <span>Jami: <strong>{totalCount}</strong> ta o'qituvchi</span>
        {totalPages > 1 && <span>Sahifa: <strong>{currentPage}</strong> / {totalPages}</span>}
      </div>

      {/* Teachers Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>№</th>
              <th>F.I.Sh</th>
              <th>Tajriba</th>
              <th>Fanlar</th>
              <th>Guruhlar</th>
              <th>Login / Parol</th>
              <th>Amallar</th>
            </tr>
          </thead>
          <tbody>
            {teachers.map((teacher, index) => (
              <tr key={teacher.id}>
                <td>{startIndex + index + 1}</td>
                <td>
                  <strong>{teacher.user.full_name}</strong>
                  <br />
                  <small className="text-muted">{teacher.user.phone || ''}</small>
                </td>
                <td>{teacher.experience_years} yil</td>
                <td>
                  <button 
                    className="btn btn-sm btn-outline"
                    onClick={() => openAssignModal(teacher, 'subjects')}
                  >
                    <BookOpen size={16} />
                    {teacher.subjects?.length || 0} ta
                  </button>
                </td>
                <td>
                  <button 
                    className="btn btn-sm btn-outline"
                    onClick={() => openAssignModal(teacher, 'groups')}
                  >
                    <Users size={16} />
                    {teacher.groups?.length || 0} ta
                  </button>
                </td>
                <td>
                  <div className="credentials-cell">
                    <code className="login-code">{teacher.user.username}</code>
                    <code className="password-code">{teacher.user.plain_password || '***'}</code>
                    <button 
                      className="btn-copy"
                      onClick={() => copyCredentials(teacher)}
                      title="Nusxalash"
                    >
                      {copiedId === teacher.id ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-icon key" 
                      onClick={() => handleResetPassword(teacher.id)}
                      title="Parolni yangilash"
                    >
                      <Key size={18} />
                    </button>
                    <button className="btn-icon edit" onClick={() => openModal(teacher)}>
                      <Edit2 size={18} />
                    </button>
                    <button className="btn-icon delete" onClick={() => handleDelete(teacher.id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {teachers.length === 0 && (
          <div className="empty-state">
            <UserCog size={48} />
            <p>O'qituvchilar topilmadi</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTeacher ? "O'qituvchini tahrirlash" : "Yangi o'qituvchi"}</h2>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Familiya *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder="Familiya"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Ism *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder="Ism"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Sharifi</label>
                  <input
                    type="text"
                    value={formData.middle_name}
                    onChange={(e) => setFormData({...formData, middle_name: e.target.value})}
                    placeholder="Sharifi"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+998 90 123 45 67"
                  />
                </div>
                <div className="form-group">
                  <label>Tajriba (yil)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.experience_years}
                    onChange={(e) => setFormData({...formData, experience_years: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              {!editingTeacher && (
                <div className="info-box">
                  <p>💡 Login va parol avtomatik yaratiladi va jadvalda ko'rsatiladi</p>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTeacher ? "Saqlash" : "Yaratish"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subjects Assignment Modal */}
      {showAssignModal === 'subjects' && selectedTeacher && (
        <AssignModal
          title={`${selectedTeacher.user.full_name} - Fanlar`}
          items={subjects}
          selectedIds={selectedTeacher.subjects?.map(s => s.id) || []}
          onSave={handleAssignSubjects}
          onClose={() => setShowAssignModal(null)}
        />
      )}

      {/* Groups Assignment Modal */}
      {showAssignModal === 'groups' && selectedTeacher && (
        <AssignModal
          title={`${selectedTeacher.user.full_name} - Guruhlar`}
          items={groups.map(g => ({ id: g.id, name: g.display_name }))}
          selectedIds={selectedTeacher.groups?.map(g => g.id) || []}
          onSave={handleAssignGroups}
          onClose={() => setShowAssignModal(null)}
        />
      )}
    </div>
  );
};

// Biriktirish modali
const AssignModal = ({ title, items, selectedIds, onSave, onClose }) => {
  const [selected, setSelected] = useState(selectedIds);

  const toggle = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(i => i !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="btn-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        <div className="subjects-list">
          {items.length === 0 ? (
            <p className="text-muted">Ma'lumotlar mavjud emas</p>
          ) : (
            items.map(item => (
              <label key={item.id} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selected.includes(item.id)}
                  onChange={() => toggle(item.id)}
                />
                <span>{item.name}</span>
              </label>
            ))
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            Bekor qilish
          </button>
          <button className="btn btn-primary" onClick={() => onSave(selected)}>
            Saqlash
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeachersPage;
